// src/components/messaging/AudioRecorder.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useTranslation } from 'react-i18next';

interface AudioRecorderProps {
  onSend: (audioBlob: Blob) => void;
  onCancel: () => void;
  maxDuration?: number; // in seconds
}

type RecordingState = 'idle' | 'recording' | 'preview';

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onSend,
  onCancel,
  maxDuration = 120, // 2 minutes
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<RecordingState>('idle');
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check browser support
  const isSupported = useCallback(() => {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof window.MediaRecorder !== 'undefined'
    );
  }, []);

  // Get the best supported MIME type for recording
  const getBestMimeType = useCallback((): string => {
    // Preferred order: ogg (Firefox), webm (Chrome), mp4 (Safari)
    const types = ['audio/ogg', 'audio/webm', 'audio/mp4', 'audio/mpeg'];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return 'audio/webm'; // fallback
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!isSupported()) {
      setError(t('messaging.browserNotSupported', 'Votre navigateur ne supporte pas l\'enregistrement audio'));
      return;
    }

    // Get the best supported MIME type
    const mimeType = getBestMimeType();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const recordedBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(recordedBlob);
        setAudioUrl(URL.createObjectURL(recordedBlob));
        setState('preview');
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setState('recording');
      setDuration(0);
      setError(null);

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration((prev) => {
          if (prev >= maxDuration - 1) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError(t('messaging.microphonePermissionDenied', 'Permission du microphone refusée'));
      } else {
        setError(t('messaging.recordingError', 'Erreur lors de l\'enregistrement'));
      }
      console.error('[AudioRecorder] Error:', err);
    }
  }, [isSupported, getBestMimeType, maxDuration, t]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    stopRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setState('idle');
    setDuration(0);
    onCancel();
  }, [stopRecording, audioUrl, onCancel]);

  // Toggle preview playback
  const togglePlayback = useCallback(() => {
    if (!audioRef.current || !audioUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, audioUrl]);

  // Send audio
  const sendAudio = useCallback(() => {
    if (audioBlob) {
      onSend(audioBlob);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioBlob(null);
      setAudioUrl(null);
      setState('idle');
      setDuration(0);
    }
  }, [audioBlob, audioUrl, onSend]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [audioUrl]);

  // Handle audio ended
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [audioUrl]);

  // Idle state - show mic button
  if (state === 'idle') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={startRecording}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            },
          }}
          title={t('messaging.tapToRecord', 'Appuyez pour enregistrer')}
        >
          <MicIcon />
        </IconButton>
        {error && (
          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Recording state
  if (state === 'recording') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flex: 1,
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
          borderRadius: 2,
          px: 2,
          py: 1,
        }}
      >
        {/* Pulsing mic icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'error.main',
            animation: `${pulse} 1.5s ease-in-out infinite`,
          }}
        >
          <MicIcon sx={{ color: 'white', fontSize: 18 }} />
        </Box>

        {/* Recording text and duration */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>
            {t('messaging.recording', 'Enregistrement...')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDuration(duration)} / {formatDuration(maxDuration)}
          </Typography>
        </Box>

        {/* Stop button */}
        <IconButton
          onClick={stopRecording}
          size="small"
          sx={{
            backgroundColor: 'error.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'error.dark',
            },
          }}
        >
          <StopIcon />
        </IconButton>

        {/* Cancel button */}
        <IconButton
          onClick={cancelRecording}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  // Preview state
  if (state === 'preview') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flex: 1,
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          borderRadius: 2,
          px: 2,
          py: 1,
        }}
      >
        {/* Hidden audio element for preview */}
        {audioUrl && <audio ref={audioRef} src={audioUrl} />}

        {/* Play/Pause button */}
        <IconButton
          onClick={togglePlayback}
          size="small"
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        {/* Duration */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
            {t('messaging.voiceMessage', 'Message vocal')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDuration(duration)}
          </Typography>
        </Box>

        {/* Send button */}
        <IconButton
          onClick={sendAudio}
          size="small"
          sx={{
            backgroundColor: 'success.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'success.dark',
            },
          }}
        >
          <SendIcon />
        </IconButton>

        {/* Cancel button */}
        <IconButton
          onClick={cancelRecording}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return null;
};

export default AudioRecorder;
